/**
 * Cortex Status Manager
 * 
 * Manages real-time cortex execution status for network visualization.
 * Connects to Cortex API SSE stream and tracks status of all cortices.
 * 
 * Status types:
 * - idle: Not yet activated (default)
 * - pending: Queued to run
 * - waiting: Waiting for dependencies
 * - active: Currently executing
 * - blocked: Blocked by activation criteria, tier, etc.
 * - skipped: Skipped (disabled, already run, etc.)
 * - completed: Finished successfully
 * - failed: Execution failed
 */

import { streamCortex, type CortexRequest, type StreamCallbacks } from '../cortex-api/client.js';

export type CortexStatus = 
  | 'idle' 
  | 'pending' 
  | 'waiting' 
  | 'active' 
  | 'blocked' 
  | 'skipped' 
  | 'completed' 
  | 'failed';

export interface CortexStatusData {
  status: CortexStatus;
  waitingFor?: string[];
  reason?: string;
  details?: string;
  timestamp: number;
}

export type StatusUpdateCallback = (cortexId: string, status: CortexStatusData) => void;

class CortexStatusManager {
  private statusMap: Map<string, CortexStatusData> = new Map();
  private updateCallbacks: Set<StatusUpdateCallback> = new Set();
  private currentStream: AbortController | null = null;
  private isStreaming: boolean = false;

  /**
   * Initialize all cortices to idle status
   */
  initializeCortices(cortexIds: string[]): void {
    for (const id of cortexIds) {
      if (!this.statusMap.has(id)) {
        this.statusMap.set(id, {
          status: 'idle',
          timestamp: Date.now(),
        });
      }
    }
  }

  /**
   * Get current status for a cortex
   */
  getStatus(cortexId: string): CortexStatusData | null {
    return this.statusMap.get(cortexId) || null;
  }

  /**
   * Get all statuses
   */
  getAllStatuses(): Map<string, CortexStatusData> {
    return new Map(this.statusMap);
  }

  /**
   * Subscribe to status updates
   */
  onStatusUpdate(callback: StatusUpdateCallback): () => void {
    this.updateCallbacks.add(callback);
    // Return unsubscribe function
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  /**
   * Update status and notify subscribers
   */
  private updateStatus(cortexId: string, status: CortexStatusData): void {
    this.statusMap.set(cortexId, status);
    
    // Notify all subscribers
    for (const callback of this.updateCallbacks) {
      try {
        callback(cortexId, status);
      } catch (error) {
        console.error('[CortexStatusManager] Error in status update callback:', error);
      }
    }
  }

  /**
   * Start streaming cortex execution status
   * This connects to the SSE stream and updates status in real-time
   */
  startStreaming(request: CortexRequest): void {
    // Don't start multiple streams
    if (this.isStreaming && this.currentStream) {
      console.warn('[CortexStatusManager] Stream already active, aborting previous stream');
      this.currentStream.abort();
    }

    this.isStreaming = true;

    // Reset all statuses to idle when starting a new stream
    for (const [id, status] of this.statusMap.entries()) {
      if (status.status !== 'idle') {
        this.updateStatus(id, {
          status: 'idle',
          timestamp: Date.now(),
        });
      }
    }

    const callbacks: StreamCallbacks = {
      onCortexStarted: (event) => {
        this.updateStatus(event.cortex, {
          status: 'active',
          timestamp: event.timestamp,
        });
      },

      onCortexWaiting: (event) => {
        this.updateStatus(event.cortex, {
          status: 'waiting',
          waitingFor: event.waitingFor,
          timestamp: event.timestamp,
        });
      },

      onCortexBlocked: (event) => {
        this.updateStatus(event.cortex, {
          status: 'blocked',
          reason: event.reason,
          details: event.details,
          timestamp: event.timestamp,
        });
      },

      onCortexSkipped: (event) => {
        this.updateStatus(event.cortex, {
          status: 'skipped',
          reason: event.reason,
          details: event.details,
          timestamp: event.timestamp,
        });
      },

      onInsufficientCredits: async (event) => {
        // Handle insufficient credits
        this.updateStatus(event.cortex, {
          status: 'blocked',
          reason: 'insufficient_credits',
          details: {
            requiredCents: event.requiredCents,
            currentBalanceCents: event.currentBalanceCents,
            shortfallCents: event.shortfallCents,
          },
          timestamp: event.timestamp,
        });
        
        // Show UI modal
        const { handleInsufficientCredits } = await import('../../services/balance/insufficient-credits-handler.js');
        handleInsufficientCredits(event);
      },

      onCortexCompleted: (event) => {
        this.updateStatus(event.cortex, {
          status: 'completed',
          timestamp: event.timestamp,
        });
      },

      onError: (event) => {
        // Mark all active cortices as failed if there's a general error
        // Individual cortex errors are handled by onCortexCompleted with error flag
        console.error('[CortexStatusManager] Stream error:', event.error);
      },

      onComplete: () => {
        this.isStreaming = false;
        this.currentStream = null;
        console.log('[CortexStatusManager] Stream completed');
      },
    };

    // Start the stream
    streamCortex(request, callbacks).then((abortController) => {
      this.currentStream = abortController;
    }).catch((error) => {
      console.error('[CortexStatusManager] Failed to start stream:', error);
      this.isStreaming = false;
      this.currentStream = null;
    });
  }

  /**
   * Stop streaming
   */
  stopStreaming(): void {
    if (this.currentStream) {
      this.currentStream.abort();
      this.currentStream = null;
    }
    this.isStreaming = false;
  }

  /**
   * Reset all statuses to idle
   */
  reset(): void {
    for (const [id] of this.statusMap.entries()) {
      this.updateStatus(id, {
        status: 'idle',
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Clear all statuses
   */
  clear(): void {
    this.statusMap.clear();
    this.stopStreaming();
    this.updateCallbacks.clear();
  }
}

// Singleton instance
export const cortexStatusManager = new CortexStatusManager();


